import { motion, AnimatePresence } from 'framer-motion'
import { Phone, Target, Trash2, MessageSquare, X, CheckCircle2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { BdrLead } from '@/types'
import type { BdrCloser } from '@/types'
import {
  resultadosTentativa,
  canais,
  panelSurface,
  labelCaps,
  fieldControl,
  actionTones,
} from './bdrLeadDetailsTheme'

export function BdrLeadDetailsRegSection({
  selectedLead,
  closers,
  showRegTentativa,
  setShowRegTentativa,
  regTentativa,
  setRegTentativa,
  savingTentativa,
  handleRegTentativa,
}: {
  selectedLead: BdrLead
  closers: BdrCloser[]
  showRegTentativa: boolean
  setShowRegTentativa: (v: boolean) => void
  regTentativa: Record<string, unknown>
  setRegTentativa: (v: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  savingTentativa: boolean
  handleRegTentativa: () => void
}) {
  function handleQuickAction(resultado: string, canal?: string) {
    setRegTentativa((prev: Record<string, unknown>) => ({
      ...prev,
      resultado,
      canal: (canal || prev.canal || 'WhatsApp') as string,
      ...(resultado === 'Reunião Agendada' && !prev.email
        ? { email: selectedLead.email || '' }
        : {}),
      ...(resultado === 'Reunião Agendada' && !prev.dores
        ? { dores: selectedLead.dores || selectedLead.problema || '' }
        : {}),
    }))
    setShowRegTentativa(true)
  }

  function handleResultadoChange(resultado: string) {
    setRegTentativa((prev: Record<string, unknown>) => ({
      ...prev,
      resultado,
      ...(resultado === 'Reunião Agendada' && !prev.dores
        ? { dores: selectedLead.dores || selectedLead.problema || '' }
        : {}),
    }))
  }

  return (
    <>
        {/* Reg Tentativa Form */}
        <AnimatePresence>
          {showRegTentativa && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className={cn(panelSurface, 'p-5 space-y-4')}>
                <div className="flex items-center justify-between">
                  <h4 className="text-text-primary font-bold text-sm">Registrar Tentativa</h4>
                  <button
                    onClick={() => setShowRegTentativa(false)}
                    className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 text-text-secondary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Canal */}
                  <div className="flex flex-col gap-1.5">
                    <label className={labelCaps}>Canal</label>
                    <select
                      value={(regTentativa.canal as string) || ''}
                      onChange={e => setRegTentativa((prev: any) => ({ ...prev, canal: e.target.value }))}
                      className={cn(fieldControl, 'appearance-none')}
                    >
                      <option value="">Selecione...</option>
                      {canais.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Resultado */}
                  <div className="flex flex-col gap-1.5">
                    <label className={labelCaps}>Resultado</label>
                    <select
                      value={(regTentativa.resultado as string) || ''}
                      onChange={e => handleResultadoChange(e.target.value)}
                      className={cn(fieldControl, 'appearance-none')}
                    >
                      <option value="">Selecione...</option>
                      {resultadosTentativa.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Reunião Agendada fields */}
                {regTentativa.resultado === 'Reunião Agendada' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5 sm:col-span-2">
                        <label className={labelCaps}>E-mail *</label>
                        <input
                          type="email"
                          value={(regTentativa.email as string) || ''}
                          onChange={e => setRegTentativa((prev: any) => ({ ...prev, email: e.target.value }))}
                          className={fieldControl}
                          placeholder="email@empresa.com"
                          required
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className={labelCaps}>Closer *</label>
                        <select
                          value={(regTentativa.closerId as string) || ''}
                          onChange={e => setRegTentativa((prev: any) => ({ ...prev, closerId: e.target.value }))}
                          className={cn(fieldControl, 'appearance-none')}
                          required
                        >
                          <option value="">
                            {closers.length === 0
                              ? 'Nenhum closer disponível'
                              : 'Selecione...'}
                          </option>
                          {closers.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        {closers.length === 0 && (
                          <p className="text-[10px] text-amber-700 dark:text-amber-400/90">
                            Cadastre usuários com perfil Closer ou Manager ativos, ou reinicie a página.
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className={labelCaps}>Data *</label>
                        <input
                          type="date"
                          value={(regTentativa.dataReuniao as string) || ''}
                          onChange={e => setRegTentativa((prev: any) => ({ ...prev, dataReuniao: e.target.value }))}
                          className={fieldControl}
                          required
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className={labelCaps}>Hora *</label>
                        <input
                          type="time"
                          value={(regTentativa.horaReuniao as string) || ''}
                          onChange={e => setRegTentativa((prev: any) => ({ ...prev, horaReuniao: e.target.value }))}
                          className={fieldControl}
                          required
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className={labelCaps}>Dores / Problema identificado *</label>
                      <textarea
                        value={(regTentativa.dores as string) || ''}
                        onChange={e => setRegTentativa((prev: any) => ({ ...prev, dores: e.target.value }))}
                        placeholder="Descreva as dores, problemas ou necessidades que motivaram o agendamento..."
                        rows={4}
                        required
                        minLength={10}
                        className={cn(fieldControl, 'resize-none min-h-[110px]')}
                      />
                      <p className="text-[10px] text-zinc-600 dark:text-text-muted">
                        Obrigatório para handoff ao Closer (mínimo 10 caracteres).
                      </p>
                    </div>
                  </div>
                )}

                {/* Observações — demais resultados; na conversão usa Dores acima */}
                {regTentativa.resultado !== 'Reunião Agendada' && (
                  <div className="flex flex-col gap-1.5">
                    <label className={labelCaps}>Observações</label>
                    <textarea
                      value={(regTentativa.obs as string) || ''}
                      onChange={e => setRegTentativa((prev: any) => ({ ...prev, obs: e.target.value }))}
                      placeholder="Notas sobre a tentativa..."
                      rows={3}
                      className={cn(fieldControl, 'resize-none min-h-[88px]')}
                    />
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={handleRegTentativa}
                  disabled={savingTentativa}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all min-h-[44px]',
                    savingTentativa
                      ? 'bg-accent/50 text-white/50 cursor-not-allowed'
                      : 'bg-accent text-white hover:brightness-110'
                  )}
                >
                  {savingTentativa ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}
                  {regTentativa.resultado === 'Reunião Agendada' ? 'Converter Lead' : 'Registrar Tentativa'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Hub */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Registrar', icon: Phone, resultado: 'Reunião Agendada', tone: 'teal' as const },
            { label: 'Converter', icon: Target, resultado: 'Reunião Agendada', tone: 'accent' as const },
            { label: 'WhatsApp', icon: MessageSquare, resultado: 'Follow-up Agendado', canal: 'WhatsApp', tone: 'green' as const },
            { label: 'Descartar', icon: Trash2, resultado: 'Sem Sucesso', tone: 'red' as const },
          ].map(action => (
            <button
              key={action.label}
              onClick={() => handleQuickAction(action.resultado, action.canal)}
              className={cn(
                'flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all min-h-[44px] shadow-sm',
                actionTones[action.tone],
              )}
            >
              <action.icon size={20} strokeWidth={2.25} />
              <span className="text-[11px] font-black uppercase tracking-wider">{action.label}</span>
            </button>
          ))}
        </div>
    </>
  )
}
